import React, { type JSX } from "react";

import { isDevelopment } from "@/utils/helpers";

import {
  useGetFirstTenantQuery,
  useIdentifyTenantByDomainNameQuery,
} from "./api";
import {
  getLocalStorageTenantId,
  setLocalStorageTenantId,
} from "./localStorage";
import { TenantNotFound, WaitingForTenant } from "./ui";

/**
 * The tenant guard that is used in production. This guard will identify the tenant by the domain name and store it in local storage.
 */
export default function TenantGuard(props: {
  children: JSX.Element;
}): JSX.Element {
  // The default state of this depends on if we already have a tenant id in local storage
  const [hasTenant, setHasTenant] = React.useState(
    getLocalStorageTenantId() !== null,
  );

  const domainName = window.location.hostname;
  const dev = isDevelopment();
  const byDomainQuery = useIdentifyTenantByDomainNameQuery(domainName, {
    enabled: !hasTenant && !dev,
  });
  const firstTenantQuery = useGetFirstTenantQuery({
    enabled: !hasTenant && dev,
  });
  const identifyTenantQuery = dev ? firstTenantQuery : byDomainQuery;
  const tenant = identifyTenantQuery.data;

  // Stored during render, not in an effect: the children's queries fire from
  // their own effects, which run before ours, and the tenant header reads local
  // storage at request time.
  if (tenant && !hasTenant) {
    setLocalStorageTenantId(tenant.identifier);
  }
  React.useEffect(() => {
    if (tenant && !hasTenant) setHasTenant(true);
  }, [tenant, hasTenant]);

  if (identifyTenantQuery.isLoading) {
    return <WaitingForTenant />;
  }

  if (identifyTenantQuery.isError) {
    return <TenantNotFound />;
  }

  return hasTenant || tenant ? props.children : <TenantNotFound />;
}
